import { useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { awaPublicWorkspaceKeys, awaUserKeys } from '@/queries/awa';
import { AWAGlobalApiService } from '@/services/global-api/awa-global-api.service';
import { useAWAStore } from '@/stores/awa.store';
import { stripAWAPath } from '@/utils/awa';
import { convertImageUrl } from '@/utils/image-url';

import logo from '../../assets/gearbelt-light.svg';
import HomeIcon from '../icons/home';
import LogoutIcon from '../icons/logout';
import { Toaster } from '../ui/toaster';

function AWALayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const queryClient = useQueryClient();
  const { workspace, setWorkspace, authTokens, setAuthTokens } = useAWAStore();

  const isMaintenancePage = useMemo(() => location.pathname.includes('maintenance'), [location.pathname]);
  const isInspectionPage = useMemo(() => location.pathname.includes('inspection'), [location.pathname]);

  const { mutateAsync: revokeToken } = useMutation({
    mutationFn: async (token: string) => {
      await AWAGlobalApiService.revokeRefreshToken(token);
    },
  });

  async function handleLogout() {
    queryClient.invalidateQueries({ queryKey: awaPublicWorkspaceKeys.all });
    queryClient.invalidateQueries({ queryKey: awaUserKeys.all });
    if (authTokens?.refreshToken) {
      await revokeToken(authTokens.refreshToken).catch(() => {
        console.warn('Failed to revoke refresh token');
      });
    }
    setWorkspace(null);
    setAuthTokens(null);
    navigate(`/auth/login?workspaceId=${workspaceId}&redirectTo=${stripAWAPath(location.pathname, true)}`);
  }

  return (
    <div className={cn('flex flex-1 flex-col', isMaintenancePage && 'bg-grey-1')}>
      <div className="mx-auto grid w-full max-w-3xl grid-cols-3 items-center px-4 py-6">
        <div>
          {!(isMaintenancePage || isInspectionPage) && (
            <Link to={`/${workspaceId}/maintenance`}>
              <HomeIcon size={36} />
            </Link>
          )}
        </div>
        <div className="flex items-center justify-center gap-2">
          {workspace?.logoUrl && (
            <Link to={`/${workspaceId}/maintenance`}>
              <img src={convertImageUrl(workspace.logoUrl)} alt="Gearbelt" className="max-h-16 w-full" />
            </Link>
          )}
        </div>
        <div className="flex items-center justify-end">
          <button onClick={handleLogout}>
            <LogoutIcon size={36} />
          </button>
        </div>
      </div>
      <div className="mx-auto w-full max-w-3xl flex-1">
        <Outlet />
      </div>

      <footer className="flex items-center justify-center p-4">
        <img src={logo} alt="Gearbelt" className="h-4" />
      </footer>

      <Toaster />
    </div>
  );
}

export default AWALayout;
